import type { BusinessType } from '@app/shared';

/**
 * Типовые услуги отрасли парами [рус, узб] — смысловой перевод, а не дословный.
 *
 * Название услуги выбирается ТОЛЬКО из этого списка (21.09.2026, решение владельца):
 * клиенту нужно понять, на что он записывается, а оба языка приходят из одной пары,
 * и переводить нечего. Не хватает услуги — добавьте пару сюда.
 *
 * Узбекские формулировки стоит показать носителю: правятся здесь же, код не трогается.
 * `Record` без `Partial`: новая отрасль без словаря не соберётся.
 */
export const SERVICE_PHRASES: Readonly<Record<BusinessType, readonly (readonly [string, string])[]>> = {
  barbershop: [
    ['Мужская стрижка', 'Erkaklar soch turmagi'],
    ['Детская стрижка', 'Bolalar soch turmagi'],
    ['Стрижка машинкой', 'Mashinkada soch olish'],
    ['Стрижка бороды', 'Soqol turmagi'],
    ['Бритьё', 'Soqol olish'],
    ['Стрижка и борода', 'Soch va soqol turmagi'],
    ['Укладка', 'Soch turmaklash'],
    ['Окрашивание волос', 'Soch boʻyash'],
  ],
  beauty: [
    ['Маникюр с покрытием', 'Qoplamali manikyur'],
    ['Маникюр', 'Manikyur'],
    ['Педикюр', 'Pedikyur'],
    ['Женская стрижка', 'Ayollar soch turmagi'],
    ['Окрашивание волос', 'Soch boʻyash'],
    ['Укладка', 'Soch turmaklash'],
    ['Наращивание ресниц', 'Kiprik uzaytirish'],
    ['Коррекция бровей', 'Qosh tuzatish'],
    ['Макияж', 'Makiyaj'],
    ['Депиляция', 'Depilyatsiya'],
  ],
  dental: [
    ['Консультация стоматолога', 'Stomatolog maslahati'],
    ['Профессиональная чистка зубов', 'Tishlarni professional tozalash'],
    ['Лечение кариеса', 'Kariyesni davolash'],
    ['Пломба', 'Plomba qoʻyish'],
    ['Удаление зуба', 'Tish sugʻurish'],
    ['Отбеливание зубов', 'Tishlarni oqartirish'],
    ['Рентген зуба', 'Tish rentgeni'],
    ['Имплантация', 'Implantatsiya'],
  ],
  massage: [
    ['Классический массаж спины', 'Klassik orqa massaji'],
    ['Массаж всего тела', 'Butun tana massaji'],
    ['Массаж шеи и плеч', 'Boʻyin va yelka massaji'],
    ['Лечебный массаж', 'Davolovchi massaj'],
    ['Расслабляющий массаж', 'Tinchlantiruvchi massaj'],
    ['Антицеллюлитный массаж', 'Antitsellyulit massaj'],
    ['Детский массаж', 'Bolalar massaji'],
  ],
  autoservice: [
    ['Замена масла', 'Moy almashtirish'],
    ['Шиномонтаж', 'Shina montaji'],
    ['Диагностика', 'Diagnostika'],
    ['Развал-схождение', 'Gʻildiraklarni sozlash'],
    ['Замена тормозных колодок', 'Tormoz kolodkalarini almashtirish'],
    ['Ремонт ходовой', 'Yurish qismini taʼmirlash'],
    ['Заправка кондиционера', 'Konditsionerga freon quyish'],
    ['Мойка', 'Avtomobil yuvish'],
  ],
  tutor: [
    ['Урок математики', 'Matematika darsi'],
    ['Урок литературы', 'Adabiyot darsi'],
    ['Английский язык', 'Ingliz tili'],
    ['Русский язык', 'Rus tili'],
    ['Узбекский язык', 'Oʻzbek tili'],
    ['Физика', 'Fizika'],
    ['Химия', 'Kimyo'],
    ['Биология', 'Biologiya'],
    ['История', 'Tarix'],
    ['Информатика', 'Informatika'],
    ['Подготовка к IELTS', 'IELTSga tayyorlov'],
    ['Подготовка к поступлению', 'Oliygohga kirishga tayyorlov'],
    ['Пробный урок', 'Sinov darsi'],
  ],
  vet: [
    ['Приём ветеринара', 'Veterinar qabuli'],
    ['Вакцинация', 'Emlash'],
    ['Стерилизация', 'Sterilizatsiya'],
    ['Кастрация', 'Kastratsiya'],
    ['Обработка от паразитов', 'Parazitlarga qarshi ishlov berish'],
    ['Анализ крови', 'Qon tahlili'],
    ['УЗИ', 'UTT (ultratovush tekshiruvi)'],
    ['Чипирование', 'Chip oʻrnatish'],
  ],
  grooming: [
    ['Стрижка собаки', 'Itni qirqish'],
    ['Стрижка кошки', 'Mushukni qirqish'],
    ['Комплексный груминг', 'Toʻliq gruming'],
    ['Мытьё', 'Choʻmiltirish'],
    ['Стрижка когтей', 'Tirnoq olish'],
    ['Чистка ушей', 'Quloq tozalash'],
  ],
  doctor: [
    ['Первичный приём', 'Birlamchi qabul'],
    ['Повторный приём', 'Takroriy qabul'],
    ['Консультация', 'Maslahat'],
    ['Вызов на дом', 'Uyga chaqirish'],
    ['УЗИ', 'UTT (ultratovush tekshiruvi)'],
    ['ЭКГ', 'EKG'],
    ['Анализы', 'Tahlillar'],
    ['Справка', 'Maʼlumotnoma'],
  ],
};

/**
 * Словарь только для автоперевода «Другой услуги»: в выпадающий список не попадает,
 * чтобы список остался коротким. Сработает, если владелец набрал фразу целиком
 * (регистр и пробелы по краям не важны). Узбекский стоит вычитать носителю.
 */
export const EXTRA_PHRASES: Readonly<Record<BusinessType, readonly (readonly [string, string])[]>> = {
  barbershop: [
    ['Стрижка', 'Soch turmagi'],
    ['Стрижка под ноль', 'Sochni taqir olish'],
    ['Модельная стрижка', 'Model soch turmagi'],
    ['Фейд', 'Feyd'],
    ['Оформление бороды', 'Soqolni shakllantirish'],
    ['Бритьё опасной бритвой', 'Ustara bilan soqol olish'],
    ['Королевское бритьё', 'Qirollik soqol olish'],
    ['Камуфляж седины', 'Oq sochni boʻyash'],
    ['Окрашивание бороды', 'Soqol boʻyash'],
    ['Мытьё головы', 'Bosh yuvish'],
    ['Уход за лицом', 'Yuz parvarishi'],
    ['Удаление волос воском', 'Mum bilan tuk olish'],
    ['Отец и сын', 'Ota va oʻgʻil'],
    ['Стрижка усов', 'Moʻylov turmagi'],
  ],
  beauty: [
    ['Стрижка', 'Soch turmagi'],
    ['Детская стрижка', 'Bolalar soch turmagi'],
    ['Стрижка чёлки', 'Chyolka qirqish'],
    ['Мелирование', 'Melirovaniye'],
    ['Тонирование', 'Tonlash'],
    ['Кератин', 'Keratin'],
    ['Ламинирование волос', 'Sochni laminatsiyalash'],
    ['Причёска', 'Pricheska'],
    ['Свадебная причёска', 'Toʻy soch turmagi'],
    ['Вечерний макияж', 'Kechki makiyaj'],
    ['Свадебный макияж', 'Toʻy makiyaji'],
    ['Наращивание ногтей', 'Tirnoq uzaytirish'],
    ['Снятие покрытия', 'Qoplamani olib tashlash'],
    ['Покрытие гель-лаком', 'Gel-lak qoplash'],
    ['Ламинирование ресниц', 'Kiprik laminatsiyasi'],
    ['Окрашивание бровей', 'Qosh boʻyash'],
    ['Ламинирование бровей', 'Qosh laminatsiyasi'],
    ['Шугаринг', 'Shugaring'],
    ['Чистка лица', 'Yuz tozalash'],
    ['Уход за лицом', 'Yuz parvarishi'],
    ['Пилинг', 'Piling'],
  ],
  dental: [
    ['Консультация', 'Maslahat'],
    ['Осмотр', 'Koʻrik'],
    ['Лечение каналов', 'Kanallarni davolash'],
    ['Лечение пульпита', 'Pulpitni davolash'],
    ['Удаление зуба мудрости', 'Aql tishini sugʻurish'],
    ['Коронка', 'Toj (koronka)'],
    ['Виниры', 'Vinirlar'],
    ['Брекеты', 'Breketlar'],
    ['Протезирование', 'Protezlash'],
    ['Детский стоматолог', 'Bolalar stomatologi'],
    ['Лечение дёсен', 'Milklarni davolash'],
    ['Снятие зубного камня', 'Tish toshini olib tashlash'],
    ['Панорамный снимок', 'Panoramali rentgen'],
  ],
  massage: [
    ['Массаж', 'Massaj'],
    ['Массаж спины', 'Orqa massaji'],
    ['Массаж ног', 'Oyoq massaji'],
    ['Массаж головы', 'Bosh massaji'],
    ['Массаж лица', 'Yuz massaji'],
    ['Спортивный массаж', 'Sport massaji'],
    ['Лимфодренажный массаж', 'Limfodrenaj massaji'],
    ['Массаж стоп', 'Oyoq kafti massaji'],
    ['Тайский массаж', 'Tay massaji'],
    ['Массаж камнями', 'Tosh bilan massaj'],
    ['Баночный массаж', 'Banka massaji'],
    ['Хиджама', 'Hijoma'],
    ['Мануальная терапия', 'Manual terapiya'],
    ['Обёртывание', 'Tanani oʻrash'],
    ['Сауна', 'Sauna'],
  ],
  autoservice: [
    ['Замена фильтров', 'Filtrlarni almashtirish'],
    ['Замена масла в коробке', 'Karobka moyini almashtirish'],
    ['Замена антифриза', 'Antifriz almashtirish'],
    ['Компьютерная диагностика', 'Kompyuter diagnostikasi'],
    ['Ремонт двигателя', 'Dvigatel taʼmiri'],
    ['Ремонт коробки передач', 'Uzatmalar qutisini taʼmirlash'],
    ['Замена ремня ГРМ', 'GRM tasmasini almashtirish'],
    ['Замена свечей', 'Svechalarni almashtirish'],
    ['Замена аккумулятора', 'Akkumulyatorni almashtirish'],
    ['Автоэлектрик', 'Avtoelektrik'],
    ['Ремонт тормозов', 'Tormozni taʼmirlash'],
    ['Кузовной ремонт', 'Kuzov taʼmiri'],
    ['Покраска', 'Boʻyash'],
    ['Полировка', 'Polirovka'],
    ['Химчистка салона', 'Salonni kimyoviy tozalash'],
    ['Балансировка', 'Balansirovka'],
    ['Переобувка', 'Shina almashtirish'],
    ['Ремонт кондиционера', 'Konditsionerni taʼmirlash'],
    ['Установка газа', 'Gaz ballon oʻrnatish'],
    ['Тонировка', 'Tonirovka'],
  ],
  tutor: [
    ['Математика', 'Matematika'],
    ['Литература', 'Adabiyot'],
    ['Урок английского', 'Ingliz tili darsi'],
    ['Урок русского', 'Rus tili darsi'],
    ['Урок физики', 'Fizika darsi'],
    ['Урок химии', 'Kimyo darsi'],
    ['Урок биологии', 'Biologiya darsi'],
    ['Урок истории', 'Tarix darsi'],
    ['География', 'Geografiya'],
    ['Корейский язык', 'Koreys tili'],
    ['Немецкий язык', 'Nemis tili'],
    ['Турецкий язык', 'Turk tili'],
    ['Арабский язык', 'Arab tili'],
    ['Подготовка к школе', 'Maktabga tayyorlov'],
    ['Подготовка к ДТМ', 'DTMga tayyorlov'],
    ['Подготовка к SAT', 'SATga tayyorlov'],
    ['Подготовка к CEFR', 'CEFRga tayyorlov'],
    ['Программирование', 'Dasturlash'],
    ['Шахматы', 'Shaxmat'],
    ['Рисование', 'Rasm chizish'],
    ['Музыка', 'Musiqa'],
    ['Индивидуальный урок', 'Individual dars'],
    ['Групповой урок', 'Guruh darsi'],
    ['Онлайн-урок', 'Onlayn dars'],
  ],
  vet: [
    ['Консультация', 'Maslahat'],
    ['Осмотр', 'Koʻrik'],
    ['Прививка', 'Emlash'],
    ['Прививка от бешенства', 'Quturishga qarshi emlash'],
    ['Кастрация кота', 'Mushukni kastratsiya qilish'],
    ['Стерилизация кошки', 'Mushukni sterilizatsiya qilish'],
    ['Обработка от блох и клещей', 'Burga va kanaga qarshi ishlov berish'],
    ['Дегельминтизация', 'Gijjaga qarshi ishlov berish'],
    ['Чистка зубов', 'Tish tozalash'],
    ['Рентген', 'Rentgen'],
    ['Операция', 'Operatsiya'],
    ['Капельница', 'Tomchilatib yuborish'],
    ['Вызов на дом', 'Uyga chaqirish'],
    ['Ветпаспорт', 'Veterinariya pasporti'],
  ],
  grooming: [
    ['Стрижка', 'Qirqish'],
    ['Гигиеническая стрижка', 'Gigiyenik qirqish'],
    ['Мытьё собаки', 'Itni choʻmiltirish'],
    ['Мытьё кошки', 'Mushukni choʻmiltirish'],
    ['Вычёсывание', 'Tarash'],
    ['Тримминг', 'Trimming'],
    ['Экспресс-линька', 'Ekspress tullash'],
    ['Распутывание колтунов', 'Chigallarni yechish'],
    ['Чистка зубов', 'Tish tozalash'],
    ['Чистка параанальных желёз', 'Paraanal bezlarni tozalash'],
    ['Выставочный груминг', 'Koʻrgazma gruming'],
  ],
  doctor: [
    ['Приём врача', 'Shifokor qabuli'],
    ['Осмотр', 'Koʻrik'],
    ['Консультация терапевта', 'Terapevt maslahati'],
    ['Консультация педиатра', 'Pediatr maslahati'],
    ['Консультация кардиолога', 'Kardiolog maslahati'],
    ['Консультация гинеколога', 'Ginekolog maslahati'],
    ['Консультация невролога', 'Nevrolog maslahati'],
    ['Консультация эндокринолога', 'Endokrinolog maslahati'],
    ['Измерение давления', 'Qon bosimini oʻlchash'],
    ['Укол', 'Ukol'],
    ['Капельница', 'Tomchilatib yuborish'],
    ['Перевязка', 'Bogʻlash'],
    ['Анализ крови', 'Qon tahlili'],
    ['Онлайн-консультация', 'Onlayn maslahat'],
    ['Медосмотр', 'Tibbiy koʻrik'],
  ],
};

const norm = (value: string): string => value.trim().toLocaleLowerCase();

/**
 * Автоперевод своего названия при наборе. `from` — индекс поля, которое правят (0 — рус, 1 — узб).
 * Вернёт значение второго поля: перевод, если набранное совпало со словарём, а второе
 * поле пустое или заполнено словарём же. Своё название владельца не перетирается.
 * ponytail: словарь, не переводчик — фразу вне SERVICE_PHRASES владелец переводит сам.
 */
export function autoTranslate(
  phrases: readonly (readonly [string, string])[],
  typed: string,
  from: 0 | 1,
  other: string,
): string {
  const to = from === 0 ? 1 : 0;
  const hit = phrases.find((p) => norm(p[from]) === norm(typed));
  if (!hit) return other;
  const otherIsOurs = other.trim() === '' || phrases.some((p) => p[to] === other);
  return otherIsOurs ? hit[to] : other;
}


